// src/pages/driverMgmtPage.js
import React, { useEffect, useState } from "react";
import { api } from "../scripts/api";
import Modal from "../components/Modal";
import { useNavigate } from "react-router-dom";

// Keyed by the server's computed `bucket` (same vocabulary as /driver/roster
// and the Drivers Today page). Fall back to the stale driver_status column
// only when the server didn't return a bucket (e.g. pre-upgrade backend).
const STATUS_COLORS = {
  available: { bg: '#d1fae5', fg: '#065f46', label: 'AVAILABLE' },
  on_move:   { bg: '#dbeafe', fg: '#1e40af', label: 'ON MOVE' },
  scheduled: { bg: '#fef3c7', fg: '#92400e', label: 'SCHEDULED' },
  off_today: { bg: '#f3f4f6', fg: '#6b7280', label: 'OFF TODAY' },
  off_duty:  { bg: '#f3f4f6', fg: '#6b7280', label: 'OFF DUTY' },
};

// Prefer the live bucket if the backend returned it; otherwise use the
// cached column so older backends still render something.
function statusKey(d) {
  return d.bucket || d.driver_status || 'off_duty';
}

export default function DriversPage() {
  const nav = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [birthday, setBirthday] = useState("");
  const [passportNum, setPassportNum] = useState("");
  const [passportExp, setPassportExp] = useState("");
  const [dealerPlate, setDealerPlate] = useState("");
  const [dealerExp, setDealerExp] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [driverStatus, setDriverStatus] = useState("available");
  const [homeTerminal, setHomeTerminal] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadDrivers(); }, []);

  const loadDrivers = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/driver");
      setDrivers(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load drivers.");
      setDrivers([]);
    } finally {
      setBusy(false);
    }
  };

  const createOrUpdateDriver = async () => {
    if (!fullName.trim()) { setMsg("Full name required."); return; }
    const payload = {
      FullName: fullName, Email: email, Phone: phone, Birthday: birthday,
      PassportNum: passportNum, PassportExp: passportExp,
      DealerPlate: dealerPlate, DealerExp: dealerExp,
      current_location: currentLocation, driver_status: driverStatus,
      home_terminal: homeTerminal || null,
    };
    setBusy(true); setMsg("");
    try {
      if (editMode) {
        await api.patch(`/driver/${selectedDriver.DriverId}`, payload);
        setMsg("Driver updated.");
      } else {
        await api.post("/driver", payload);
        setMsg("Driver created.");
      }
      resetForm(); loadDrivers(); closeModal();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Operation failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteDriver = async (DriverId) => {
    if (!window.confirm("Delete this driver?")) return;
    setBusy(true);
    try {
      await api.delete(`/driver/${DriverId}`);
      setMsg("Driver deleted."); loadDrivers();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed.");
    } finally { setBusy(false); }
  };

  const openCreateModal = () => { resetForm(); setEditMode(false); setSelectedDriver({}); };

  const openEditModal = (driver) => {
    setFullName(driver.FullName || ""); setEmail(driver.Email || "");
    setPhone(driver.Phone || ""); setBirthday(driver.Birthday || "");
    setPassportNum(driver.PassportNum || ""); setPassportExp(driver.PassportExp || "");
    setDealerPlate(driver.DealerPlate || ""); setDealerExp(driver.DealerExp || "");
    setCurrentLocation(driver.current_location || "");
    setDriverStatus(driver.driver_status || "available");
    setHomeTerminal(driver.home_terminal || "");
    setEditMode(true); setSelectedDriver(driver);
  };

  const closeModal = () => { setSelectedDriver(null); setEditMode(false); };

  const resetForm = () => {
    setFullName(""); setEmail(""); setPhone(""); setBirthday("");
    setPassportNum(""); setPassportExp(""); setDealerPlate(""); setDealerExp("");
    setCurrentLocation(""); setDriverStatus("available");
    setHomeTerminal("");
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>Driver Management</h2>
        <button className="btn-outline" onClick={loadDrivers} disabled={busy}>Refresh</button>
        <button className="btn-outline" onClick={() => nav("/admin/driver-calendar")}>Calendar</button>
        <button className="btn" onClick={openCreateModal} disabled={busy}>Add Driver</button>
      </div>

      {msg && <div className="muted" style={{ marginTop: 10 }}>{msg}</div>}

      {/* Status board */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Driver Status Board</div>
        {busy && <div className="muted">Loading…</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          {drivers.map((d) => {
            const key = statusKey(d);
            const col = STATUS_COLORS[key] || STATUS_COLORS.off_duty;
            const label = col.label || String(key).toUpperCase();
            return (
              <div key={d.DriverId} style={{ borderRadius: 10, padding: "10px 14px", background: col.bg, color: col.fg }}>
                <div style={{ fontWeight: 900, fontSize: 15 }}>{d.FullName}</div>
                <div style={{ fontSize: 12, marginTop: 2, textTransform: 'uppercase', fontWeight: 700 }}>
                  {label}
                </div>
                {d.active_move && (
                  <div style={{ fontSize: 11, marginTop: 4, opacity: 0.9 }}>
                    🚛 Trip #{d.active_move.TripId}{d.active_move.destination ? ` → ${d.active_move.destination}` : ''}
                  </div>
                )}
                {!d.active_move && d.next_scheduled && (
                  <div style={{ fontSize: 11, marginTop: 4, opacity: 0.9 }}>
                    📅 Next: {String(d.next_scheduled.md_start || '').slice(0, 10)}{d.next_scheduled.destination ? ` → ${d.next_scheduled.destination}` : ''}
                  </div>
                )}
                {!d.active_move && !d.next_scheduled && Array.isArray(d.unavailability) && d.unavailability.length > 0 && (
                  <div style={{ fontSize: 11, marginTop: 4, opacity: 0.9 }}>
                    🌴 {d.unavailability[0].type}{d.unavailability[0].reason ? ` · ${d.unavailability[0].reason}` : ''}
                  </div>
                )}
                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
                  📍 {d.current_location || 'No location set'}
                </div>
                {d.home_terminal && (
                  <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7, fontWeight: 700, letterSpacing: 0.3 }}>
                    🏠 {d.home_terminal}
                  </div>
                )}
              </div>
            );
          })}
          {!busy && drivers.length === 0 && <div className="muted">No drivers found.</div>}
        </div>
      </div>

      {/* Full driver list */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>All Drivers</div>
        <div style={{ display: "grid", gap: 10 }}>
          {drivers.map((d) => {
            const key = statusKey(d);
            const col = STATUS_COLORS[key] || STATUS_COLORS.off_duty;
            const label = col.label || String(key).toUpperCase();
            return (
              <div key={d.DriverId} className="card" style={{ background: "rgba(255,255,255,0.02)" }}>
                <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>{d.FullName}
                      <span style={{ marginLeft: 10, fontSize: 11, padding: '2px 8px', borderRadius: 99, background: col.bg, color: col.fg, fontWeight: 700 }}>
                        {label}
                      </span>
                    </div>
                    <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
                      {d.Email || '—'} • {d.Phone || '—'}
                    </div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      📍 {d.current_location || 'Location not set'} • Plate: {d.DealerPlate || '—'}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <button className="btn-outline" onClick={() => openEditModal(d)}>Edit</button>
                    <button className="btn-outline" onClick={() => deleteDriver(d.DriverId)}>Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
          {!busy && drivers.length === 0 && <div className="muted">No drivers found.</div>}
        </div>
      </div>

      {selectedDriver && (
        <Modal onClose={closeModal}>
          <h2>{editMode ? "Edit Driver" : "Add Driver"}</h2>
          <div style={{ display: "grid", gap: 10 }}>
            <input className="input" placeholder="Full Name *" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <input className="input" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input className="input" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <input className="input" placeholder="Birthday" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
              <input className="input" placeholder="Passport #" value={passportNum} onChange={(e) => setPassportNum(e.target.value)} />
              <input className="input" placeholder="Passport Exp" value={passportExp} onChange={(e) => setPassportExp(e.target.value)} />
            </div>
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <input className="input" placeholder="Dealer Plate" value={dealerPlate} onChange={(e) => setDealerPlate(e.target.value)} />
              <input className="input" placeholder="Dealer Exp" value={dealerExp} onChange={(e) => setDealerExp(e.target.value)} />
            </div>
            <div style={{ borderTop: '1px solid #eee', paddingTop: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Status & Location</div>
              <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                <select className="input" value={driverStatus} onChange={(e) => setDriverStatus(e.target.value)}>
                  <option value="available">Available</option>
                  <option value="on_move">On Move</option>
                  <option value="off_duty">Off Duty</option>
                </select>
                <input className="input" placeholder="Current location (e.g. Calgary Yard)" value={currentLocation} onChange={(e) => setCurrentLocation(e.target.value)} />
                <input
                  className="input"
                  placeholder="Home terminal (e.g. Calgary, Winnipeg)"
                  value={homeTerminal}
                  onChange={(e) => setHomeTerminal(e.target.value)}
                  title="Used to route moves to regionally-appropriate drivers"
                />
              </div>
            </div>

            {/* Driver portal PIN — separate panel so it's never set by accident during a normal edit */}
            {editMode && selectedDriver?.DriverId && (
              <DriverPinPanel
                driverId={selectedDriver.DriverId}
                hasPin={!!selectedDriver.has_pin}
                onChanged={() => loadDrivers()}
              />
            )}

            <div className="row" style={{ gap: 10 }}>
              <button className="btn" onClick={createOrUpdateDriver} disabled={busy}>{editMode ? "Update" : "Create"}</button>
              <button className="btn-outline" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Driver portal PIN panel (embedded inside the driver edit modal) ────────
// Kept separate from the main form so PINs only change when the admin
// explicitly sets / clears them here. Never shows the current PIN (it's a
// bcrypt hash server-side).
function DriverPinPanel({ driverId, hasPin, onChanged }) {
  const [pin, setPin]     = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState("");
  const [err, setErr]     = useState("");

  const setOrChange = async () => {
    setErr(""); setMsg("");
    if (!/^\d{3,12}$/.test(pin)) return setErr("PIN must be 3–12 digits.");
    if (pin !== confirm)         return setErr("PINs don't match.");
    setBusy(true);
    try {
      await api.patch(`/driver/${driverId}/pin`, { pin });
      setPin(""); setConfirm("");
      setMsg(hasPin ? "PIN updated." : "PIN set.");
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to update PIN.");
    } finally { setBusy(false); }
  };

  const clearPin = async () => {
    if (!window.confirm("Clear this driver's PIN? They won't be able to sign in to the portal until a new PIN is set.")) return;
    setErr(""); setMsg(""); setBusy(true);
    try {
      await api.patch(`/driver/${driverId}/pin`, { pin: "" });
      setMsg("PIN cleared.");
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to clear PIN.");
    } finally { setBusy(false); }
  };

  return (
    <div
      style={{
        border: "1px solid var(--card-border)",
        borderRadius: 10,
        padding: 14,
        background: "rgba(127,127,127,0.04)",
        margin: "4px 0 8px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 14 }}>Driver Portal PIN</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {hasPin
              ? "✓ PIN is set. Enter a new PIN below to change it."
              : "No PIN yet. Driver can't log into the portal until one is set."}
          </div>
        </div>
        {hasPin && (
          <button className="btn-outline" onClick={clearPin} disabled={busy} style={{ color: "#dc2626", borderColor: "#fca5a5" }}>
            Clear PIN
          </button>
        )}
      </div>

      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <input
          className="input"
          type="password"
          inputMode="numeric"
          maxLength={12}
          placeholder="New PIN (3–12 digits)"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          style={{ flex: "1 1 150px" }}
          autoComplete="new-password"
        />
        <input
          className="input"
          type="password"
          inputMode="numeric"
          maxLength={12}
          placeholder="Confirm PIN"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
          style={{ flex: "1 1 150px" }}
          autoComplete="new-password"
        />
        <button className="btn" onClick={setOrChange} disabled={busy || !pin}>
          {hasPin ? "Change PIN" : "Set PIN"}
        </button>
      </div>

      {err && <div style={{ marginTop: 8, color: "#dc2626", fontSize: 13, fontWeight: 700 }}>{err}</div>}
      {msg && <div style={{ marginTop: 8, color: "#15803d", fontSize: 13, fontWeight: 700 }}>{msg}</div>}
    </div>
  );
}